/**
 * ShiftTZPreview — surfaces the effective site-local time for a
 * datetime-local input pair whenever the site's timezone differs from
 * the browser's. Prevents "HQ scheduling far-flung site → shift lands
 * on the wrong wall-clock hour" bugs by showing both zones side-by-side
 * before the operator hits Save.
 *
 * Rendered as a col-span-2 block inside the grid layout used by both
 * ShiftFormModal (create) and EditShiftModal (edit-single). Renders
 * nothing until a site is picked AND a start time is set.
 */
export function ShiftTZPreview({ site, startLocal, endLocal, testIdPrefix = "shift-tz-preview" }) {
  if (!site || !startLocal) return null;
  const siteTz = site.timezone || null;
  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch { return null; }
  })();
  const tzMismatch = siteTz && browserTz && siteTz !== browserTz;
  const fmtInZone = (localValue, zone) => {
    if (!localValue) return "";
    const d = new Date(localValue);
    if (Number.isNaN(d.getTime())) return "";
    try {
      return d.toLocaleString("en-US", {
        timeZone: zone,
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      });
    } catch { return ""; }
  };
  const startSite = siteTz ? fmtInZone(startLocal, siteTz) : "";
  const endSite = siteTz ? fmtInZone(endLocal, siteTz) : "";

  return (
    <div
      data-testid={testIdPrefix}
      className={`col-span-2 border-l-2 pl-3 py-2 ${
        tzMismatch ? "border-l-[#FF9F0A] bg-[#FF9F0A]/5" : "border-l-[#34C759] bg-[#34C759]/5"
      }`}
    >
      {!siteTz ? (
        <div className="text-[11px] text-[#FF9F0A]" data-testid={`${testIdPrefix}-nozone`}>
          <span className="font-mono uppercase tracking-widest">Note ·</span>{" "}
          <span className="font-medium">{site.name}</span> has no timezone set.
          Times will be interpreted in your browser&rsquo;s local time
          {browserTz ? <> (<span className="font-mono">{browserTz}</span>)</> : null}.
          Set the site&rsquo;s timezone in Site Settings for accurate scheduling.
        </div>
      ) : tzMismatch ? (
        <div className="space-y-1 text-[11px]" data-testid={`${testIdPrefix}-mismatch`}>
          <div>
            <span className="label-mute mr-2">At site ({siteTz})</span>
            <span className="font-mono text-[#34C759]">
              {startSite}
              {endSite && <> → {endSite}</>}
            </span>
          </div>
          <div className="text-zinc-500">
            <span className="label-mute mr-2">Your browser ({browserTz})</span>
            <span className="font-mono">
              {fmtInZone(startLocal, browserTz)}
              {endLocal && <> → {fmtInZone(endLocal, browserTz)}</>}
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF9F0A]">
            You typed times in your browser&rsquo;s zone — verify the site-local row above is what you meant.
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-[#34C759] font-mono" data-testid={`${testIdPrefix}-match`}>
          <span className="label-mute mr-2 not-italic">Site local</span>
          {startSite}
          {endSite && <> → {endSite}</>}
        </div>
      )}
    </div>
  );
}
